import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScissorsIcon, XIcon } from 'lucide-react';
interface Service {
  title: string;
  price: string;
  priceNumber: number;
  emoji: string;
}
interface Toast {
  id: number;
  name: string;
  city: string;
  service: Service;
}
const SERVICES: Service[] = [{
  title: 'Haircut',
  price: '$25',
  priceNumber: 25,
  emoji: '✂️'
}, {
  title: 'Skin Fade',
  price: '$30',
  priceNumber: 30,
  emoji: '💈'
}, {
  title: 'Beard Trim',
  price: '$15',
  priceNumber: 15,
  emoji: '🪒'
}, {
  title: 'Haircut + Beard',
  price: '$40',
  priceNumber: 40,
  emoji: '💈'
}, {
  title: 'Kids Cut',
  price: '$20',
  priceNumber: 20,
  emoji: '✂️'
}, {
  title: 'Hot Towel Shave',
  price: '$35',
  priceNumber: 35,
  emoji: '🪒'
}, {
  title: 'Line Up',
  price: '$15',
  priceNumber: 15,
  emoji: '✂️'
}];
const NAMES = ['Carlos', 'Miguel', 'Luis', 'Jose', 'Juan', 'Diego', 'Andres', 'Ricardo', 'Fernando', 'Roberto', 'Alex', 'David', 'Daniel', 'Marco', 'Antonio', 'Eduardo', 'Alejandro', 'Gabriel', 'Sebastian', 'Adrian', 'Kevin', 'Bryan', 'Jason', 'Michael', 'Chris', 'James', 'Ryan', 'Tyler', 'Jordan', 'Marcus'];
const CITIES = ['Hialeah, FL', 'Miami, FL', 'Miami Gardens, FL', 'Doral, FL', 'Kendall, FL', 'Homestead, FL', 'Coral Gables, FL', 'Westchester, FL', 'Sweetwater, FL', 'Opa-locka, FL', 'North Miami, FL', 'Medley, FL', 'Miramar, FL', 'Hollywood, FL', 'Pembroke Pines, FL', 'Fort Lauderdale, FL'];
function scanPageServices(): Service[] {
  try {
    const priceRegex = /\$\s?(\d+(?:\.\d{2})?)/g;
    const found: Service[] = [];
    const cards = document.querySelectorAll('[data-service], .service-card, [class*="service"]');
    cards.forEach(card => {
      const text = card.textContent || '';
      const priceMatch = text.match(/\$\s?(\d+)/);
      if (priceMatch) {
        const titleEl = card.querySelector('h2, h3, h4, strong');
        const title = titleEl?.textContent?.trim() || 'Service';
        const priceNumber = parseFloat(priceMatch[1]);
        found.push({
          title,
          price: `$${priceNumber}`,
          priceNumber,
          emoji: '💈'
        });
      }
    });
    return found.length > 0 ? found : SERVICES;
  } catch {
    return SERVICES;
  }
}
let toastCounter = 0;
export function BookingToast() {
  const [toast, setToast] = useState<Toast | null>(null);
  const nameHistoryRef = useRef<string[]>([]);
  const queueRef = useRef<Toast[]>([]);
  const isShowingRef = useRef(false);
  const servicesRef = useRef<Service[]>(SERVICES);
  const pickName = useCallback(() => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pool[Math.floor(Math.random() * pool.length)];
    nameHistoryRef.current = [...history.slice(-9), name];
    return name;
  }, []);
  const showNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      isShowingRef.current = false;
      return;
    }
    isShowingRef.current = true;
    const next = queueRef.current.shift()!;
    setToast(next);
    setTimeout(() => {
      setToast(null);
      setTimeout(showNext, 600);
    }, 4000);
  }, []);
  const enqueue = useCallback(() => {
    const services = servicesRef.current;
    const service = services[Math.floor(Math.random() * services.length)];
    const name = pickName();
    const city = CITIES[Math.floor(Math.random() * CITIES.length)];
    const newToast: Toast = {
      id: ++toastCounter,
      name,
      city,
      service
    };
    queueRef.current.push(newToast);
    if (!isShowingRef.current) {
      showNext();
    }
  }, [pickName, showNext]);
  useEffect(() => {
    servicesRef.current = scanPageServices();
    const firstTimer = setTimeout(() => {
      enqueue();
      const repeat = () => {
        const delay = 8000 + Math.random() * 7000;
        setTimeout(() => {
          enqueue();
          repeat();
        }, delay);
      };
      repeat();
    }, 3000);
    return () => clearTimeout(firstTimer);
  }, [enqueue]);
  const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return <div className="fixed bottom-6 left-6 z-[9998] pointer-events-none" aria-live="polite" aria-atomic="true" data-id="element-6">
      <AnimatePresence data-id="element-7">
        {toast && <motion.div key={toast.id} initial={prefersReduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} animate={prefersReduced ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={prefersReduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto" data-id="element-8">
            <div className="flex items-center gap-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded-xl shadow-2xl px-4 py-3 max-w-[300px] w-full" data-id="element-9">
              {/* Icon */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#c41e3a]/15 border border-[#c41e3a]/30 flex items-center justify-center text-lg" data-id="element-10">
                {toast.service.emoji}
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0" data-id="element-11">
                <p className="text-white text-xs font-semibold leading-tight truncate" data-id="element-12">
                  {toast.name}{' '}
                  <span className="text-gray-400 font-normal" data-id="element-13">
                    in {toast.city}
                  </span>
                </p>
                <p className="text-gray-300 text-xs mt-0.5 leading-tight" data-id="element-14">
                  just booked{' '}
                  <span className="text-[#c41e3a] font-semibold" data-id="element-15">
                    {toast.service.title}
                  </span>
                </p>
                <p className="text-[#c9a84c] text-xs font-bold mt-0.5" data-id="element-16">
                  {toast.service.price}
                </p>
              </div>

              {/* Barber pole mini accent */}
              <div className="flex-shrink-0 flex flex-col gap-[2px] opacity-60" data-id="element-17">
                {[...Array(5)].map((_, i) => <div key={i} className="w-1.5 h-1" style={{
              background: i % 3 === 0 ? '#c41e3a' : i % 3 === 1 ? '#ffffff' : '#003087'
            }} data-id="element-18" />)}
              </div>
            </div>

            {/* Progress bar */}
            <motion.div className="h-0.5 bg-[#c41e3a] rounded-full mt-1 origin-left" initial={{
          scaleX: 1
        }} animate={{
          scaleX: 0
        }} transition={{
          duration: 4,
          ease: 'linear'
        }} data-id="element-19" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}